import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("gm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, acceptPrivacy = false) => {
    const { data } = await api.post("/auth/register", { email, password, name, accept_privacy: acceptPrivacy });
    localStorage.setItem("gm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completeGoogle = async (sessionId) => {
    const { data } = await api.post("/auth/session", null, {
      headers: { "X-Session-ID": sessionId },
    });
    localStorage.setItem("gm_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("Logout API call failed (clearing local state anyway):", err); }
    localStorage.removeItem("gm_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, completeGoogle, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
